import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { ShopService } from '@app/services/shops/shop.service';
import { EssentialFeatureGate } from '@app/services/shops/shop.model';
import { FeatureAccessService } from '@app/services/feature-access/feature-access.service';
import { UpgradeToEnterpriseDialogService } from '@app/services/feature-access/upgrade-to-enterprise-dialog.service';
import { FeatureKey, FeatureTab } from '@pdqdev/pdq-shared/dist/utils/features-manifest';
import { environment } from '@environment/environment';
import { catchError, filter, first, map, of, timeout } from 'rxjs';

const SECTION_GATE_MAP: Record<string, EssentialFeatureGate> = {
  engage: EssentialFeatureGate.ENGAGE,
  convert: EssentialFeatureGate.CONVERT,
  deliver: EssentialFeatureGate.DELIVER,
};

const ITEM_GATE_MAP: Record<string, EssentialFeatureGate> = {
  'ab-tests': EssentialFeatureGate.AB_TESTS,
};

const PATH_TO_TAB: Record<string, FeatureTab> = {
  engage: FeatureTab.ENGAGE,
  convert: FeatureTab.CONVERT,
  retain: FeatureTab.RETAIN,
  deliver: FeatureTab.DELIVER,
};

const PATH_TO_FEATURE: Record<string, FeatureKey> = {
  'ab-tests': FeatureKey.AB_TESTS,
};

const FALLBACK_URL = '/optimize/strategy-catalog';
const ITEM_FALLBACK_URL = '/optimize/dashboard';

@Injectable({
  providedIn: 'root',
})
export class EssentialsPlanGuard {
  constructor(
    private shopService: ShopService,
    private router: Router,
    private featureAccess: FeatureAccessService,
    private upgradeToEnterpriseDialog: UpgradeToEnterpriseDialogService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot) {
    if (environment.FEATURE_ACCESS_GATING_ENABLED && this.featureAccess.loaded) {
      return this.checkFeatureAccess(route);
    }
    return this.shopService.shop$.pipe(
      filter((shop) => !!shop),
      first(),
      timeout(5000),
      map((shop) => {
        if (!shop.isEssentials) return true;

        const features = shop.essentialFeatures;
        if (!features) return true;

        // Check section-level gating (engage, convert, deliver)
        const sectionPath = route.url[0]?.path;
        const sectionGate = SECTION_GATE_MAP[sectionPath];
        if (sectionGate && features[sectionGate] === false) {
          this.openUpgrade();
          return this.router.parseUrl(FALLBACK_URL);
        }

        // Check item-level gating (ab-tests within optimize)
        const childPath = route.firstChild?.url[0]?.path || route.url[1]?.path;
        if (childPath) {
          const itemGate = ITEM_GATE_MAP[childPath];
          if (itemGate && features[itemGate] === false) {
            this.openUpgrade();
            return this.router.parseUrl(ITEM_FALLBACK_URL);
          }
        }

        return true;
      }),
      catchError(() => of(true)),
    );
  }

  private checkFeatureAccess(route: ActivatedRouteSnapshot) {
    const tab = PATH_TO_TAB[route.url[0]?.path];
    if (tab && this.featureAccess.isTabBlocked(tab)) {
      this.openUpgrade();
      return this.router.parseUrl(FALLBACK_URL);
    }

    const path = route.url[0]?.path;
    const childPath = route.firstChild?.url[0]?.path || route.url[1]?.path;
    const feature = (childPath ? PATH_TO_FEATURE[childPath] : undefined) || PATH_TO_FEATURE[path];
    if (feature && !this.featureAccess.isEnabled(feature)) {
      this.openUpgrade();
      return this.router.parseUrl(ITEM_FALLBACK_URL);
    }

    return true;
  }

  private openUpgrade(): void {
    this.upgradeToEnterpriseDialog.open();
  }
}
